import React, { useState, useEffect } from 'react';
import { XMarkIcon, ClockIcon, ArrowsRightLeftIcon, CloudArrowUpIcon, CloudArrowDownIcon, CalendarIcon } from '@heroicons/react/24/outline';
import { usageApi, UsageSummaryResponse } from '../api/usage';
import { useError } from '../contexts/ErrorContext';
import { extractErrorMessage } from '../utils/errorHandling';
import LoadingSkeleton from './LoadingSkeleton';

interface UsageHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type TimePeriod = 1 | 7 | 30;

const UsageHistoryModal: React.FC<UsageHistoryModalProps> = ({ isOpen, onClose }) => {
  const { addError } = useError();
  const [usageData, setUsageData] = useState<UsageSummaryResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState<TimePeriod>(30);

  useEffect(() => {
    if (isOpen) {
      fetchUsageData(selectedPeriod);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, selectedPeriod]);

  const fetchUsageData = async (periodDays: number) => {
    try {
      setIsLoading(true);
      const response = await usageApi.getUsageSummary(periodDays);
      setUsageData(response.data);
    } catch (error) {
      const apiError = extractErrorMessage(error, 'Failed to fetch usage history');
      addError(apiError.message);
    } finally {
      setIsLoading(false);
    }
  };

  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`;
  };

  const formatDuration = (minutes: number): string => {
    if (minutes < 1) return '< 1 min';
    if (minutes < 60) return `${Math.round(minutes)} min`;
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    if (mins === 0) return `${hours}h`;
    return `${hours}h ${mins}m`;
  };

  const getPeriodLabel = (period: TimePeriod): string => {
    switch (period) {
      case 1:
        return 'Today';
      case 7:
        return 'Last 7 Days';
      case 30:
        return 'Last 30 Days';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        />

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full sm:p-6">
          <div className="absolute top-0 right-0 pt-4 pr-4">
            <button
              onClick={onClose}
              className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          <div className="sm:flex sm:items-start w-full">
            <div className="w-full">
              <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                Connection Usage History
              </h3>

              {/* Time Period Selector */}
              <div className="mb-6">
                <div className="flex space-x-2">
                  {([1, 7, 30] as TimePeriod[]).map((period) => (
                    <button
                      key={period}
                      onClick={() => setSelectedPeriod(period)}
                      className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                        selectedPeriod === period
                          ? 'bg-primary-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {getPeriodLabel(period)}
                    </button>
                  ))}
                </div>
              </div>

              {isLoading ? (
                <LoadingSkeleton type="list" count={3} />
              ) : usageData ? (
                <>
                  {/* Summary Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <div className="bg-blue-50 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-blue-600">Total Data Shared</p>
                          <p className="text-2xl font-bold text-blue-900 mt-1">
                            {formatBytes(usageData.total_bytes_shared)}
                          </p>
                        </div>
                        <ArrowsRightLeftIcon className="h-8 w-8 text-blue-400" />
                      </div>
                    </div>

                    <div className="bg-green-50 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-green-600">Total Sessions</p>
                          <p className="text-2xl font-bold text-green-900 mt-1">
                            {usageData.total_sessions_hosted}
                          </p>
                        </div>
                        <CalendarIcon className="h-8 w-8 text-green-400" />
                      </div>
                    </div>

                    <div className="bg-purple-50 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-purple-600">Total Time</p>
                          <p className="text-2xl font-bold text-purple-900 mt-1">
                            {formatDuration(usageData.total_hosting_duration_minutes)}
                          </p>
                        </div>
                        <ClockIcon className="h-8 w-8 text-purple-400" />
                      </div>
                    </div>
                  </div>

                  {/* Per-Friend Usage */}
                  <div>
                    <h4 className="text-md font-medium text-gray-900 mb-3">
                      Usage by Friend ({usageData.unique_friends_count})
                    </h4>

                    {usageData.friends.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        No usage data for this period
                      </div>
                    ) : (
                      <div className="space-y-3 max-h-96 overflow-y-auto">
                        {usageData.friends.map((friendStats) => (
                          <div
                            key={friendStats.friend.id}
                            className="bg-gray-50 rounded-lg p-4 hover:bg-gray-100 transition-colors"
                          >
                            <div className="flex items-center justify-between mb-2">
                              <div>
                                <div className="text-sm font-medium text-gray-900">
                                  {friendStats.friend.display_name}
                                </div>
                                {friendStats.friend.username && (
                                  <div className="text-xs text-gray-500">
                                    @{friendStats.friend.username}
                                  </div>
                                )}
                              </div>
                              <div className="text-right">
                                <div className="text-sm font-semibold text-gray-900">
                                  {formatBytes(friendStats.total_bytes)}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {friendStats.total_sessions} session{friendStats.total_sessions !== 1 ? 's' : ''}
                                </div>
                              </div>
                            </div>

                            <div className="grid grid-cols-3 gap-3 mt-3 pt-3 border-t border-gray-200">
                              <div className="flex items-center">
                                <CloudArrowUpIcon className="h-4 w-4 text-gray-400 mr-1" />
                                <div>
                                  <div className="text-xs text-gray-500">Uploaded</div>
                                  <div className="text-xs font-medium text-gray-700">
                                    {formatBytes(friendStats.bytes_uploaded)}
                                  </div>
                                </div>
                              </div>

                              <div className="flex items-center">
                                <CloudArrowDownIcon className="h-4 w-4 text-gray-400 mr-1" />
                                <div>
                                  <div className="text-xs text-gray-500">Downloaded</div>
                                  <div className="text-xs font-medium text-gray-700">
                                    {formatBytes(friendStats.bytes_downloaded)}
                                  </div>
                                </div>
                              </div>

                              <div className="flex items-center">
                                <ClockIcon className="h-4 w-4 text-gray-400 mr-1" />
                                <div>
                                  <div className="text-xs text-gray-500">Duration</div>
                                  <div className="text-xs font-medium text-gray-700">
                                    {formatDuration(friendStats.total_duration_minutes)}
                                  </div>
                                </div>
                              </div>
                            </div>

                            {friendStats.last_session_at && (
                              <div className="text-xs text-gray-400 mt-2">
                                Last session: {new Date(friendStats.last_session_at).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  Failed to load usage data
                </div>
              )}
            </div>
          </div>

          {/* Close button */}
          <div className="mt-6">
            <button
              type="button"
              onClick={onClose}
              className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:text-sm"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UsageHistoryModal;
